import { Filters, Loader } from 'components';
import content from 'content.json';
import React, { useEffect, useRef, useState } from 'react';
import { Col, Image } from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Link, useParams } from 'react-router-dom';
import { Formatter } from 'services';
import styled from 'styled-components';
import { CollectionItem, CollectionV3 } from 'types/collection';
import {
    BoardsQueryParameters,
    FilterOptionsParameters,
    FilterParams,
    FilterStruct,
    StylingRoomSection
} from 'types/styling';

import { StylingRoomBoards } from '../../../redux/reducers';
import Stylist from '../../../services/Stylist';
import { Empty, FilterBadges } from '../components';
import { TRouteParams } from '../types';

const { styling } = content;

const SubTabsNav = styled.div`
    display: flex;
    align-items: center;

    a {
        color: #000000;
        margin-right: 23px;
        font-size: 14px;

        &.current,
        &:hover {
            font-weight: bold;
            color: #000000;
        }
    }
`;

interface IBoards {
    loading: boolean;
    boards: StylingRoomBoards;
    clearBoards: () => void;
    loadBoards: (params: BoardsQueryParameters) => void;
    loadBoardsFilters: (fields: Record<string, string>) => void;
    filters: FilterStruct[];
    updateFilters: (filter: FilterParams, isSelected: boolean) => void;
    selectedFilters: Record<string, FilterOptionsParameters[]>;
    addToCanvas: (item: CollectionItem) => void;
}

export const Boards: React.FC<IBoards> = ({
    loading,
    boards,
    clearBoards,
    loadBoards,
    loadBoardsFilters,
    filters,
    selectedFilters,
    updateFilters,
    addToCanvas
}) => {
    const { subTab: collectionType = 'styleboard' } = useParams<TRouteParams>();
    const [jump, setJump] = useState(false);
    const resultsRef = useRef<HTMLDivElement>(null);

    const fetchData = () => {
        const styleTags = selectedFilters.style_tags?.map(({ key }) => key as string);

        loadBoards({
            type: collectionType,
            styleTags
        });
    };

    const addCollectionItemsToCanvas = async (id: string) => {
        const { data } = await Stylist.getCollection(id);
        const collection = data as CollectionV3;

        collection.items.forEach(({ id, gid, type, imageUrl, price = 0, currency }) =>
            addToCanvas({
                uuid: id,
                gid,
                type,
                pictureUrl: imageUrl,
                price,
                currency,
                unique: id
            } as CollectionItem)
        );
    };

    const onFilterChange = (filter: FilterParams) => {
        const isSelected =
            !!selectedFilters[filter.key] &&
            selectedFilters[filter.key].findIndex((item: FilterParams | FilterOptionsParameters) =>
                !Array.isArray(filter.value) ? item.key === filter.value.key : false
            ) > -1;
        updateFilters(filter, isSelected);
    };

    useEffect(() => {
        loadBoardsFilters({});
    }, []);

    useEffect(() => {
        clearBoards();
        fetchData();
    }, [collectionType, selectedFilters]);

    return (
        <div className="styling-room-boards">
            <Col className="filters-column">
                <p className="total">
                    {boards ? (
                        styling.boards.replace(
                            '%count%',
                            Formatter.number(boards.pageInfo?.totalItems || 0)
                        )
                    ) : (
                        <br />
                    )}
                </p>

                <Filters
                    className={
                        selectedFilters.category && !selectedFilters.category.length
                            ? 'categories-all'
                            : ''
                    }
                    filters={filters}
                    selected={selectedFilters}
                    setFilter={onFilterChange}
                    showSelection={true}
                />
            </Col>

            <div id="results" className="results col" ref={resultsRef}>
                <div className="results-bar">
                    <SubTabsNav>
                        {styling.subTabs.boards.map(({ value, text }) => (
                            <Link
                                key={value}
                                className={value === collectionType ? 'current' : ''}
                                to={`${value}`}>
                                {text}
                            </Link>
                        ))}
                    </SubTabsNav>
                    {Object.keys(selectedFilters).length > 0 && (
                        <FilterBadges
                            selectedFilters={selectedFilters}
                            clearFilter={({ filter }) => filter && onFilterChange(filter)}
                            view={StylingRoomSection.shop}
                        />
                    )}
                </div>
                {boards?.items &&
                    (boards.items.length ? (
                        <InfiniteScroll
                            dataLength={boards.items.length}
                            onScroll={() =>
                                setJump(
                                    resultsRef.current ? resultsRef.current.scrollTop > 0 : false
                                )
                            }
                            next={fetchData}
                            hasMore={boards.pageInfo.hasNextPage}
                            style={{ overflow: 'hidden' }}
                            loader={
                                <div key="loading" className="board loading">
                                    <Image src={styling.assets.loading_logo} />
                                </div>
                            }>
                            {boards.items.map(({ id, imageUrl }) => (
                                <div key={id} className="board">
                                    <Image
                                        src={imageUrl}
                                        onClick={() => addCollectionItemsToCanvas(id)}
                                    />
                                </div>
                            ))}
                        </InfiniteScroll>
                    ) : (
                        <Empty tab={collectionType} />
                    ))}
                {loading && <Loader />}
            </div>

            <div
                className={`top-btn ${jump ? 'show' : 'hide'}`}
                onClick={() => (resultsRef.current ? resultsRef.current.scrollTo(0, 0) : null)}>
                {styling.top}
            </div>
        </div>
    );
};
